import { useState } from "react";
import { formatDate } from "../../../../../api/DateUtils";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import EpaCard from "../../../../../leap-components/EpaCard";
import EowBreadCrumbs from "./../EowBreadCrumbs";
import Header from "./../Header";
import ContactUs from "./ContactUs";
import { Grid } from "../../../../../leap-components/Grid";
import CircularEconomyHomeContainer from "./../CircularEconomyHomeContainer";
import {
  GetFilteredEowsCsv,
  GetFilteredEowsFetchUrl,
} from "../../../../../api/CircularEconomy/INotifiers";
import { EpaGridOrderByFetchParams } from "../../../../../leap-components/Grid/EpaGridProps";
import { CreateStaticSelectColumnFilter } from "../../../../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";

interface ISearchResultRow {
  licence_name: string;
  licence_number: string;
  organisation: string;
  town: string;
  county: string;
}

const localAuthorityFilterValues = [
  { value: "Carlow County Council", label: "Carlow County Council" },
  { value: "Cavan County Council", label: "Cavan County Council" },
  { value: "Clare County Council", label: "Clare County Council" },
  { value: "Cork City Council", label: "Cork City Council" },
  { value: "Cork County Council", label: "Cork County Council" },
  { value: "Donegal County Council", label: "Donegal County Council" },
  { value: "Dublin City Council", label: "Dublin City Council" },
  {
    value: "Dun Laoghaire Rathdown County Council",
    label: "Dun Laoghaire Rathdown County Council",
  },
  {
    value: "Environmental Protection Agency",
    label: "Environmental Protection Agency",
  },
  { value: "Fingal County Council", label: "Fingal County Council" },
  { value: "Galway City Council", label: "Galway City Council" },
  { value: "Galway County Council", label: "Galway County Council" },
  { value: "Kerry County Council", label: "Kerry County Council" },
  { value: "Kildare County Council", label: "Kildare County Council" },
  { value: "Kilkenny County Council", label: "Kilkenny County Council" },
  { value: "Laois County Council", label: "Laois County Council" },
  { value: "Leitrim County Council", label: "Leitrim County Council" },
  {
    value: "Limerick City and County Council",
    label: "Limerick City and County Council",
  },
  { value: "Limerick County Council", label: "Limerick County Council" },
  { value: "Longford County Council", label: "Longford County Council" },
  { value: "Louth County Council", label: "Louth County Council" },
  { value: "Mayo County Council", label: "Mayo County Council" },
  { value: "Meath County Council", label: "Meath County Council" },
  { value: "Monaghan County Council", label: "Monaghan County Council" },
  {
    value: "North Tipperary County Council",
    label: "North Tipperary County Council",
  },
  { value: "Offaly County Council", label: "Offaly County Council" },
  { value: "Roscommon County Council", label: "Roscommon County Council" },
  { value: "Sligo County Council", label: "Sligo County Council" },
  {
    value: "South Dublin County Council",
    label: "South Dublin County Council",
  },
  {
    value: "South Tipperary County Council",
    label: "South Tipperary County Council",
  },
  { value: "Tipperary County Council", label: "Tipperary County Council" },
  {
    value: "Waterford City and County Council",
    label: "Waterford City and County Council",
  },
  { value: "Waterford County Council", label: "Waterford County Council" },
  { value: "Westmeath County Council", label: "Westmeath County Council" },
  { value: "Wexford County Council", label: "Wexford County Council" },
  { value: "Wicklow County Council", label: "Wicklow County Council" },
];

const typeFilterValues = [
  { value: "EU Registration", label: "EU Registration" },
  { value: "National Registration", label: "National Registration" },
  { value: "Single Case Registration", label: "Single Case Registration" },
];

const statusFilterValues = [
  { value: "Deregistered", label: "Deregistered" },
  { value: "Registered", label: "Registered" },
];

const GetUrlForNotifier = (NotifierId: string) => {
  return `/eow/${NotifierId}`;
};

const EowHome = () => {
  const [searchValue, setSearchValue] = useState("");

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const tableColumnsBig: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Reg Number",
      accessor: "reg_no",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetUrlForNotifier(rowData.notifier_id)}>
            {rowInfo.value}
          </Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Registration Date",
      accessor: "date_received",
      Filter: false,
      Cell: (rowInfo: any) => formatDate(rowInfo.value),
      sortType: "basic",
    },
    {
      Header: "Producer",
      accessor: "notifier_name",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "End-of-waste Decision Type",
      accessor: "type",
      Filter: CreateStaticSelectColumnFilter(
        typeFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Criteria Ref",
      accessor: "decision_ref_no",
      Filter: false,
    },
    {
      Header: "Material type",
      accessor: "notified_material",
      Filter: false,
    },
    {
      Header: "Link to Criteria",
      accessor: "hyperlink",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return <a href={rowInfo.value}>{rowInfo.value}</a>;
      },
    },
    {
      Header: "Waste Authorisation Ref No",
      accessor: "waste_authorisation_regno",
      Filter: false,
    },
    {
      Header: "Enforcement Authority",
      accessor: "enforcement_authority",
      Filter: CreateStaticSelectColumnFilter(
        localAuthorityFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
  ];

  const tableColumnsSmall: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Reg Number",
      accessor: "reg_no",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetUrlForNotifier(rowData.notifier_id)}>
            {rowInfo.value}
          </Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Registration Date",
      accessor: "date_received",
      Filter: false,
      Cell: (rowInfo: any) => formatDate(rowInfo.value),
      sortType: "basic",
    },
    {
      Header: "Producer",
      accessor: "notifier_name",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "End-of-waste Decision Type",
      accessor: "type",
      Filter: CreateStaticSelectColumnFilter(
        typeFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Criteria Ref",
      accessor: "decision_ref_no",
      Filter: false,
    },
    {
      Header: "Material type",
      accessor: "notified_material",
      Filter: false,
    },
    {
      Header: "Link to Criteria",
      accessor: "hyperlink",
      Filter: false,
    },
    {
      Header: "Waste Authorisation Ref No",
      accessor: "waste_authorisation_regno",
      Filter: false,
    },
    {
      Header: "Enforcement Authority",
      accessor: "enforcement_authority",
      Filter: CreateStaticSelectColumnFilter(
        localAuthorityFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
  ];

  const getFetchUrlFactory = () => {
    let fetchUrlFn = (
      pageNo: number,
      recordsPerPage: number,
      searchText: string,
      filters: any,
      orderBy?: EpaGridOrderByFetchParams
    ) => {
      return GetFilteredEowsFetchUrl(
        pageNo,
        recordsPerPage,
        searchText,
        filters,
        orderBy
      );
    };

    return fetchUrlFn;
  };

  const fetchUrl = getFetchUrlFactory();

  const orderBy = {
    orderByAscending: false,
    orderByName: "Registration Date",
    enabledOrderByColumns: ["Registration Date", "Reg Number", "Operator"],
  };

  const results = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetFilteredEowsCsv}
        />
      </div>
      <div className="d-none d-md-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetFilteredEowsCsv}
        />
      </div>
    </div>
  );

  const contentElement = (
    <div>
      <Row>
        <Col className="ps-0">
          <EpaCard title="Search End-of-waste Registers" content={results} />
        </Col>
      </Row>
      <Row>
        <Col className="ps-0">
          <ContactUs />
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      <EowBreadCrumbs />
      <div className="ps-0">
        <Header />
        <CircularEconomyHomeContainer content={contentElement} />
      </div>
    </div>
  );
};

export default EowHome;
